/**
 * LocalStorageService
 * A utility for managing Local Storage operations with JSON support.
 */
const LocalStorageService = {
  /**
   * Create or update an item in Local Storage.
   * @param key - The key under which to store the value.
   * @param value - The value to be stored (object or string).
   */
  setItem<T>(key: string, value: T): void {
    let item: string

    // Check if the value is an object and serialize it to a string if necessary
    if (typeof value === 'object' && value !== null) {
      item = JSON.stringify(value)
    } else {
      item = value as unknown as string // Ensure item is treated as a string
    }

    try {
      localStorage.setItem(key, item)
    } catch (e) {
      // QuotaExceededError — storage full
      console.error(`localStorage quota exceeded writing key "${key}"`)
    }
  },

  /**
   * Read an item from Local Storage.
   * @param key - The key of the item to retrieve.
   * @returns The parsed object or string from Local Storage.
   */
  getItem<T>(key: string): T | null {
    const item = localStorage.getItem(key)
    if (!item) return null // Return null if the item does not exist

    // Check if the item looks like JSON before parsing
    const isJSON =
      item.startsWith('{') || item.startsWith('[') || item.startsWith('"')

    if (isJSON) {
      try {
        // Try to parse the JSON string
        return JSON.parse(item) as T
      } catch (error: unknown) {
        // Explicitly type error as unknown
        // Log an error if parsing fails, and return the original string
        if (error instanceof Error) {
          console.error(
            `Failed to parse item from Local Storage: ${error.message}`
          )
        } else {
          console.error(
            'Failed to parse item from Local Storage: Unknown error'
          )
        }
        return item as unknown as T // Return as unknown to satisfy TypeScript
      }
    }

    return item as unknown as T // Return as unknown to satisfy TypeScript
  },

  /**
   * Update an item in Local Storage by merging new values.
   * @param key - The key of the item to update.
   * @param newValue - The new values to merge.
   */
  updateItem<T>(key: string, newValue: Partial<T>): void {
    const existingValue = this.getItem<T>(key) || ({} as T) // Default to an empty object if not found
    const updatedValue = { ...existingValue, ...newValue }
    this.setItem(key, updatedValue)
  },

  /**
   * Remove an item from Local Storage.
   * @param key - The key of the item to remove.
   */
  removeItem(key: string): void {
    localStorage.removeItem(key)
  },

  /**
   * Clear all items from Local Storage.
   */
  clear(): void {
    localStorage.clear()
  },
}

export default LocalStorageService